import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import QMSLayout from "../components/QMSLayout";
import OrgSelector from "../components/OrgSelector";
import { API } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search, Plus, ChevronDown, ChevronRight, FileText,
  CheckCircle2, AlertTriangle, XCircle, MinusCircle,
  Loader2, Filter, ArrowRight, FileCheck, Sparkles, Download
} from "lucide-react";

const STATUS_CONFIG = {
  compliant: { icon: CheckCircle2, color: "#4ADE80", label: "Compliant", bg: "rgba(74,222,128,0.1)" },
  partial: { icon: AlertTriangle, color: "#FBBF24", label: "Partial", bg: "rgba(251,191,36,0.1)" },
  "non-compliant": { icon: XCircle, color: "#F87171", label: "Non-Compliant", bg: "rgba(248,113,113,0.1)" },
  "not-applicable": { icon: MinusCircle, color: "#8B95A5", label: "N/A", bg: "rgba(139,149,165,0.1)" },
};

const RISK_COLORS = { low: "#4ADE80", medium: "#FBBF24", high: "#FB923C", critical: "#F87171" };

const GapAssessmentPage = () => {
  const { t } = useTranslation('audit');
  const navigate = useNavigate();
  const [standards, setStandards] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingToolkit, setGeneratingToolkit] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedFindings, setExpandedFindings] = useState({});
  const [form, setForm] = useState({
    title: "", standards: [], client_name: "", org_id: "", org_name: "", vendor_id: "", sector: "",
    scope_description: "", existing_procedures: "", certifications_held: []
  });

  useEffect(() => {
    loadStandards();
    loadAssessments();
  }, []);

  const loadStandards = async () => {
    try {
      const res = await axios.get(`${API}/standards`, { withCredentials: true });
      setStandards(res.data);
    } catch (e) { console.error(e); }
  };

  const loadAssessments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gap-assessments`, { withCredentials: true });
      setAssessments(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleGenerate = async () => {
    if (!form.title || form.standards.length === 0 || !form.client_name || !form.sector || !form.scope_description) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/gap-assessment/generate`, form, { withCredentials: true });
      setSelectedAssessment(res.data);
      setShowCreate(false);
      loadAssessments();
      setForm({ title: "", standards: [], client_name: "", org_id: "", org_name: "", vendor_id: "", sector: "", scope_description: "", existing_procedures: "", certifications_held: [] });
      // Poll for completion if status is "generating"
      if (res.data.status === "generating") {
        pollForCompletion(res.data.assessment_id);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate assessment. Please try again.");
    }
    setGenerating(false);
  };

  const pollForCompletion = async (assessmentId) => {
    const maxAttempts = 60;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await axios.get(`${API}/gap-assessments/${assessmentId}`, { withCredentials: true });
        if (res.data.status === "completed" || res.data.status === "failed") {
          setSelectedAssessment(res.data);
          loadAssessments();
          return;
        }
      } catch (e) { break; }
    }
  };

  const toggleStandard = (stdId) => {
    setForm(prev => ({
      ...prev,
      standards: prev.standards.includes(stdId)
        ? prev.standards.filter(s => s !== stdId)
        : [...prev.standards, stdId]
    }));
  };

  const toggleFinding = (idx) => {
    setExpandedFindings(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const RISK_ORDER = { critical: 0, high: 1, medium: 2, low: 3, undefined: 4 };

  const sortedFilteredFindings = [...(selectedAssessment?.findings?.filter(f =>
    filterStatus === "all" || f.status === filterStatus
  ) || [])].sort((a, b) => (RISK_ORDER[a.risk_level] ?? 4) - (RISK_ORDER[b.risk_level] ?? 4));

  const summary = selectedAssessment?.summary || {};

  const sectors = [
    "life-sciences", "automotive", "aerospace", "industrial", "iot",
    "medical-devices", "pharmaceutical", "food-safety",
    "manufacturing", "electronics", "brazil-latam", "energy",
    "chemical", "defense", "construction"
  ];

  const handleGenerateToolkit = async (assessmentId) => {
    setGeneratingToolkit(true);
    try {
      await axios.post(`${API}/gap-assessment/${assessmentId}/generate-toolkit`, {}, { withCredentials: true });
      navigate("/quality-agreement");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate toolkit. Please try again.");
    }
    setGeneratingToolkit(false);
  };

  return (
    <QMSLayout title={t('audit:gapAssessment.title')}>
      <div className="space-y-6" data-testid="gap-assessment-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-[#8B95A5] text-sm mt-1">
              {t('governance:gap.subtitle')}
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#36A9E1] hover:bg-[#2d8fbf] text-white text-sm font-medium rounded-[3px] transition-colors"
            data-testid="new-assessment-btn"
          >
            <Plus size={16} />
            {t("governance:gap.newAssessment")}
          </button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-assessment-modal">
              <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
                <h2 className="text-lg font-['Syne'] font-bold text-white">{t('governance:gap.newGapAssessment')}</h2>
                <p className="text-[#8B95A5] text-sm mt-1">{t('governance:gap.newGapAssessmentDesc')}</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t("governance:gap.assessmentTitle")}</label>
                  <input
                    value={form.title}
                    onChange={e => setForm({...form, title: e.target.value})}
                    placeholder="e.g., ISO 9001 Gap Assessment - PharmaCorp"
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                    data-testid="assessment-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('governance:gap.complianceStandards')}</label>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] p-3" data-testid="standards-multi-select">
                      {standards.map(s => (
                        <label
                          key={s.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${
                            form.standards.includes(s.id)
                              ? "bg-[rgba(54,169,225,0.15)] text-[#38BDF8]"
                              : "text-[#CBD5E1] hover:bg-[rgba(255,255,255,0.03)]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={form.standards.includes(s.id)}
                            onChange={() => toggleStandard(s.id)}
                            className="rounded border-[rgba(255,255,255,0.2)] bg-transparent text-[#36A9E1]"
                          />
                          <span className="truncate">{s.name}</span>
                        </label>
                      ))}
                    </div>
                    {form.standards.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {form.standards.map(id => {
                          const s = standards.find(x => x.id === id);
                          return s ? (
                            <span key={id} className="text-xs bg-[rgba(54,169,225,0.15)] text-[#38BDF8] px-2 py-0.5 rounded-full flex items-center gap-1">
                              {s.name}
                              <button onClick={() => toggleStandard(id)} className="hover:text-white">&times;</button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('governance:gap.sector')}</label>
                    <select
                      value={form.sector}
                      onChange={e => setForm({...form, sector: e.target.value})}
                      className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                      data-testid="sector-select"
                    >
                      <option value="">{t('governance:gap.selectSector')}</option>
                      {sectors.map(s => (
                        <option key={s} value={s}>{s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('governance:gap.clientName')}</label>
                  <OrgSelector
                    value={form.org_id}
                    onChange={(orgId, orgName) => setForm({...form, org_id: orgId, org_name: orgName, client_name: orgName || form.client_name})}
                    showVendorId={true}
                    vendorId={form.vendor_id}
                    onVendorIdChange={v => setForm({...form, vendor_id: v})}
                  />
                  {!form.org_id && (
                    <input
                      value={form.client_name}
                      onChange={e => setForm({...form, client_name: e.target.value})}
                      placeholder={t('governance:gap.clientNamePlaceholder')}
                      className="w-full mt-2 px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none"
                      data-testid="client-name-input"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('governance:gap.scopeDescription')}</label>
                  <textarea
                    value={form.scope_description}
                    onChange={e => setForm({...form, scope_description: e.target.value})}
                    placeholder="Describe the scope of the assessment, including processes, sites, and products covered..."
                    rows={3}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none resize-none"
                    data-testid="scope-input"
                  />
                </div>
                <div>
                  <label className="block text-[#CBD5E1] text-sm mb-1.5">{t('governance:gap.existingProcedures')}</label>
                  <textarea
                    value={form.existing_procedures}
                    onChange={e => setForm({...form, existing_procedures: e.target.value})}
                    placeholder="List existing quality procedures, documentation, and systems in place..."
                    rows={3}
                    className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm focus:border-[#36A9E1] outline-none resize-none"
                  />
                </div>
              </div>
              <div className="p-6 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-[#8B95A5] hover:text-white transition-colors text-sm"
                >
                  {t('common:actions.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={generating || !form.title || form.standards.length === 0 || !form.client_name || !form.sector || !form.scope_description}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#36A9E1] hover:bg-[#2d8fbf] disabled:opacity-50 text-white text-sm font-medium rounded-[3px] transition-colors"
                  data-testid="generate-assessment-btn"
                >
                  {generating ? <><Loader2 size={16} className="animate-spin" /> Analyzing...</> : <><Search size={16} /> Generate Assessment</>}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Assessment List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="text-sm font-medium text-[#8B95A5] uppercase tracking-wider">{t('governance:gap.assessments')}</h3>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
            ) : assessments.length === 0 ? (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-8 text-center">
                <FileText className="mx-auto mb-3 text-[#8B95A5]" size={32} />
                <p className="text-[#8B95A5] text-sm">{t('governance:gap.noAssessments')}</p>
                <p className="text-[#8B95A5] text-xs mt-1">{t('governance:gap.createFirst')}</p>
              </div>
            ) : (
              assessments.map(a => (
                <div
                  key={a.assessment_id}
                  onClick={() => {
                    setSelectedAssessment(a);
                    if (a.status === "generating") pollForCompletion(a.assessment_id);
                  }}
                  className={`bg-[#1F222D] border rounded-[3px] p-4 cursor-pointer transition-all hover:border-[#36A9E1]/40 ${
                    selectedAssessment?.assessment_id === a.assessment_id ? "border-[#36A9E1]" : "border-[rgba(255,255,255,0.07)]"
                  }`}
                  data-testid={`assessment-item-${a.assessment_id}`}
                >
                  <h4 className="text-white text-sm font-medium truncate">{a.title}</h4>
                  <p className="text-[#38BDF8] text-xs mt-1">{a.standard_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[#8B95A5] text-xs">{a.client_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{
                      background: (a.summary?.overall_readiness_percent || 0) >= 70 ? "rgba(74,222,128,0.15)" : "rgba(251,191,36,0.15)",
                      color: (a.summary?.overall_readiness_percent || 0) >= 70 ? "#4ADE80" : "#FBBF24"
                    }}>
                      {a.summary?.overall_readiness_percent || 0}% {t('governance:gap.ready')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Assessment Detail */}
          <div className="lg:col-span-2">
            {selectedAssessment ? (
              selectedAssessment.status === "generating" ? (
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center" data-testid="generating-indicator">
                  <Loader2 className="mx-auto mb-4 animate-spin text-[#36A9E1]" size={40} />
                  <h3 className="text-white text-lg font-['Syne'] font-bold mb-2">AI Analysis in Progress</h3>
                  <p className="text-[#8B95A5] text-sm">Analysing compliance gaps clause-by-clause against {selectedAssessment.standard_name}...</p>
                  <div className="mt-4 max-w-xs mx-auto">
                    <div className="h-1.5 bg-[#111318] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#36A9E1] to-[#4ADE80] rounded-full animate-pulse" style={{width: '60%'}} />
                    </div>
                    <p className="text-[#8B95A5] text-xs mt-2">{t('governance:gap.typicalTime')}</p>
                  </div>
                </div>
              ) : selectedAssessment.status === "failed" ? (
                <div className="bg-[#1F222D] border border-[rgba(248,113,113,0.2)] rounded-[3px] p-12 text-center">
                  <XCircle className="mx-auto mb-4 text-[#F87171]" size={40} />
                  <h3 className="text-white text-lg font-['Syne'] font-bold mb-2">{t('governance:gap.assessmentFailed')}</h3>
                  <p className="text-[#8B95A5] text-sm">{summary.executive_summary || "An error occurred during analysis. Please try again."}</p>
                </div>
              ) : (
              <div className="space-y-4">
                {/* Summary Scorecard Header */}
                <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-white font-['Syne'] font-bold">{selectedAssessment.title}</h3>
                      <p className="text-[#38BDF8] text-sm">{selectedAssessment.standard_name} | {selectedAssessment.sector}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold" style={{ color: (summary.overall_readiness_percent || 0) >= 70 ? "#10B981" : "#C9A84C" }}>
                        {summary.overall_readiness_percent || 0}%
                      </div>
                      <p className="text-[#8B95A5] text-xs">{t('governance:gap.readiness')}</p>
                    </div>
                  </div>

                  {/* Readiness bar with audit threshold */}
                  <div className="mb-4">
                    <div className="relative h-3 bg-[#111318] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{
                        width: `${Math.min(summary.overall_readiness_percent || 0, 100)}%`,
                        background: (summary.overall_readiness_percent || 0) >= 70 ? 'linear-gradient(90deg, #10B981, #38BDF8)' : 'linear-gradient(90deg, #C9A84C, #FB923C)'
                      }} />
                      <div className="absolute top-0 h-full w-px bg-white/40" style={{left: '70%'}} />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[#8B95A5] text-[10px]">{selectedAssessment?.findings?.length || 0} {t('governance:gap.findings_label')}</span>
                      <span className="text-[#8B95A5] text-[10px]">{t('governance:gap.auditThreshold')}</span>
                    </div>
                  </div>

                  {/* 4-metric scorecard */}
                  <div className="grid grid-cols-4 gap-3" data-testid="scorecard-metrics">
                    {[
                      { label: t('governance:gap.compliant'), count: summary.compliant || 0, color: "#10B981", icon: "circle-check" },
                      { label: t('governance:gap.partial'), count: summary.partial || 0, color: "#C9A84C", icon: "circle-half" },
                      { label: t('governance:gap.gapStatus'), count: summary.non_compliant || 0, color: "#EF4444", icon: "circle-x" },
                      { label: t('governance:gap.notAssessed'), count: summary.not_applicable || 0, color: "#9CA3AF", icon: "circle-minus" },
                    ].map(s => (
                      <div key={s.label} className="bg-[#111318] rounded-[3px] p-3 text-center border-t-2" style={{ borderColor: s.color }}>
                        <div className="text-lg font-bold" style={{ color: s.color }}>{s.count}</div>
                        <div className="text-[#8B95A5] text-xs">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Executive Summary */}
                {summary.executive_summary && (
                  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-5">
                    <h4 className="text-white text-sm font-medium mb-2">{t('governance:gap.executiveSummary')}</h4>
                    <p className="text-[#CBD5E1] text-sm leading-relaxed whitespace-pre-line">{summary.executive_summary}</p>
                  </div>
                )}

                {/* Critical Gaps */}
                {summary.critical_gaps?.length > 0 && (
                  <div className="bg-[rgba(248,113,113,0.05)] border border-[rgba(248,113,113,0.15)] rounded-[3px] p-4">
                    <h4 className="text-[#F87171] text-sm font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle size={14} /> {t('governance:gap.criticalGaps')}
                    </h4>
                    <ul className="space-y-1">
                      {summary.critical_gaps.map((g, i) => (
                        <li key={i} className="text-[#CBD5E1] text-sm flex items-start gap-2">
                          <ArrowRight size={12} className="mt-1 text-[#F87171] shrink-0" />
                          {g}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Quick Wins */}
                {summary.quick_wins?.length > 0 && (
                  <div className="bg-[rgba(74,222,128,0.05)] border border-[rgba(74,222,128,0.15)] rounded-[3px] p-4">
                    <h4 className="text-[#4ADE80] text-sm font-medium mb-2 flex items-center gap-2">
                      <CheckCircle2 size={14} /> {t('governance:gap.quickWins')}
                    </h4>
                    <ul className="space-y-1">
                      {summary.quick_wins.map((w, i) => (
                        <li key={i} className="text-[#CBD5E1] text-sm flex items-start gap-2">
                          <ArrowRight size={12} className="mt-1 text-[#4ADE80] shrink-0" />
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Generate QMS Toolkit from Assessment */}
                {selectedAssessment?.status === "completed" && (
                  <div className="bg-[rgba(232,168,56,0.05)] border border-[rgba(232,168,56,0.15)] rounded-[3px] p-4 flex items-center justify-between"
                    data-testid="generate-toolkit-section">
                    <div className="flex items-center gap-3">
                      <FileCheck size={20} className="text-[#E8A838]" />
                      <div>
                        <h4 className="text-white text-sm font-medium">{t('governance:gap.generateToolkit')}</h4>
                        <p className="text-[#8B95A5] text-xs">{t('governance:gap.generateToolkitDesc')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleGenerateToolkit(selectedAssessment.assessment_id)}
                      disabled={generatingToolkit}
                      className="flex items-center gap-2 px-4 py-2 bg-[#E8A838] hover:bg-[#d49730] disabled:opacity-50 text-[#111318] text-sm font-medium rounded-[3px] transition-colors"
                      data-testid="generate-toolkit-btn"
                    >
                      {generatingToolkit ? <><Loader2 size={14} className="animate-spin" /> {t('governance:gap.generating')}</> : <><Sparkles size={14} /> {t('governance:gap.generateToolkit')}</>}
                    </button>
                  </div>
                )}

                {/* PDF Export Button */}
                {selectedAssessment?.status === "completed" && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/export/gap-assessment/${selectedAssessment.assessment_id}/pdf`, {
                            withCredentials: true,
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `gap_assessment_${selectedAssessment.assessment_id}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch (err) {
                          console.error("PDF export error:", err);
                          toast.error("PDF export failed. Please try again.");
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[#181B23] text-[#C9A84C] border border-[#C9A84C]/30 rounded text-sm hover:bg-[#C9A84C]/10 transition-colors"
                      data-testid="export-pdf-btn"
                    >
                      <Download size={14} /> {t('governance:gap.exportPdf')}
                    </button>
                  </div>
                )}

                {/* Sticky Filter Bar */}
                <div className="sticky top-0 z-10 bg-[#111318] py-2 flex items-center justify-between" data-testid="findings-filter-bar">
                  <div className="flex items-center gap-2">
                    <Filter size={14} className="text-[#8B95A5]" />
                    {[
                      { id: "all", label: "All", color: "#8B95A5" },
                      { id: "compliant", label: "Compliant", color: "#10B981" },
                      { id: "partial", label: "Partial", color: "#C9A84C" },
                      { id: "non-compliant", label: "Gap", color: "#EF4444" },
                      { id: "not-applicable", label: "N/A", color: "#9CA3AF" },
                    ].map(s => (
                      <button
                        key={s.id}
                        onClick={() => setFilterStatus(s.id)}
                        className={`px-3 py-1.5 text-xs rounded-full transition-colors font-medium ${
                          filterStatus === s.id ? "text-white" : "text-[#8B95A5] hover:text-white"
                        }`}
                        style={filterStatus === s.id ? { backgroundColor: `${s.color}20`, color: s.color } : {}}
                        data-testid={`filter-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <span className="text-[#8B95A5] text-xs">{sortedFilteredFindings.length} {t('governance:gap.findings_label')}</span>
                </div>

                {/* Clause Accordion — grouped by section */}
                <div className="space-y-2" data-testid="clause-accordion">
                  {(() => {
                    // Group findings by section number (first digit of clause_id)
                    const sections = {};
                    sortedFilteredFindings.forEach((f, idx) => {
                      const sectionNum = (f.clause_id || "").split(".")[0] || "Other";
                      if (!sections[sectionNum]) sections[sectionNum] = { findings: [], gapCount: 0 };
                      sections[sectionNum].findings.push({ ...f, originalIdx: idx });
                      if (f.status === "non-compliant" || f.status === "partial") sections[sectionNum].gapCount++;
                    });

                    return Object.entries(sections).map(([section, data]) => {
                      const isExpanded = expandedFindings[`section-${section}`] !== false; // default open
                      return (
                        <div key={section} className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] overflow-hidden" data-testid={`section-${section}`}>
                          {/* Section Header */}
                          <div
                            className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                            onClick={() => setExpandedFindings(p => ({ ...p, [`section-${section}`]: !isExpanded }))}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? <ChevronDown size={14} className="text-[#8B95A5]" /> : <ChevronRight size={14} className="text-[#8B95A5]" />}
                              <span className="text-white text-sm font-semibold">{t('governance:gap.section')} {section}</span>
                              <span className="text-[#8B95A5] text-xs">{data.findings.length} clause{data.findings.length !== 1 ? "s" : ""}</span>
                            </div>
                            {data.gapCount > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[rgba(239,68,68,0.1)] text-[#EF4444] font-medium">
                                {data.gapCount} gap{data.gapCount !== 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                          {/* Section Findings */}
                          {isExpanded && (
                            <div className="border-t border-[rgba(255,255,255,0.05)]">
                              {data.findings.map((f) => {
                                const cfg = STATUS_CONFIG[f.status] || STATUS_CONFIG["not-applicable"];
                                const Icon = cfg.icon;
                                const findingKey = f.originalIdx;
                                return (
                                  <div key={findingKey} className="border-b border-[rgba(255,255,255,0.03)] last:border-b-0">
                                    <div
                                      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                                      onClick={() => toggleFinding(findingKey)}
                                    >
                                      {/* Traffic-light icon */}
                                      <Icon size={16} style={{ color: cfg.color }} />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[#38BDF8] text-xs font-mono">{f.clause_id}</span>
                                          <span className="text-white text-sm">{f.clause_title}</span>
                                        </div>
                                      </div>
                                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>
                                        {cfg.label}
                                      </span>
                                      {f.risk_level && (
                                        <span className="text-xs font-medium" style={{ color: RISK_COLORS[f.risk_level] }}>
                                          {f.risk_level.toUpperCase()}
                                        </span>
                                      )}
                                      {expandedFindings[findingKey] ? <ChevronDown size={14} className="text-[#8B95A5]" /> : <ChevronRight size={14} className="text-[#8B95A5]" />}
                                    </div>
                                    {expandedFindings[findingKey] && (
                                      <div className="px-4 pb-4 space-y-3 ml-7">
                                        {f.finding && (
                                          <div>
                                            <span className="text-[#8B95A5] text-xs uppercase tracking-wider">Finding</span>
                                            <p className="text-[#CBD5E1] text-sm mt-1">{f.finding}</p>
                                          </div>
                                        )}
                                        {f.gap_description && (
                                          <div>
                                            <span className="text-[#8B95A5] text-xs uppercase tracking-wider">{t('governance:gap.gapLabel')}</span>
                                            <p className="text-[#CBD5E1] text-sm mt-1">{f.gap_description}</p>
                                          </div>
                                        )}
                                        {f.recommendation && (
                                          <div>
                                            <span className="text-[#8B95A5] text-xs uppercase tracking-wider">{t("governance:gap.recommendation")}</span>
                                            <p className="text-[#C9A84C] text-sm mt-1">{f.recommendation}</p>
                                          </div>
                                        )}
                                        {f.evidence_needed && (
                                          <div>
                                            <span className="text-[#8B95A5] text-xs uppercase tracking-wider">{t('governance:gap.evidenceNeeded')}</span>
                                            <p className="text-[#CBD5E1] text-sm mt-1">{f.evidence_needed}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              ) /* end completed view */
            ) : (
              <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] p-12 text-center">
                <Search className="mx-auto mb-4 text-[#8B95A5]" size={40} />
                <h3 className="text-white text-lg font-['Syne'] font-bold mb-2">{t('governance:gap.selectAssessment')}</h3>
                <p className="text-[#8B95A5] text-sm">{t('governance:gap.selectAssessmentDesc')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default GapAssessmentPage;
